<template>
    <span v-tooltip:[tooltipModifier]="tooltipValue" v-bind="$attrs" :class="tooltipClasses">
        <slot />
    </span>
</template>

<script>
/**
 * ETooltip - Abstraction wrapper to replicate Buefy's b-tooltip
 *
 * Usage:
 *   <e-tooltip label="This is a helpful tooltip">
 *     <e-button>Hover me</e-button>
 *   </e-tooltip>
 *
 *   <e-tooltip label="Delete this item" position="is-bottom">
 *     <e-icon icon="trash" />
 *   </e-tooltip>
 *
 *   <e-tooltip label="Long text here" multilined size="is-medium">
 *     <e-icon icon="info" />
 *   </e-tooltip>
 *
 */
export default {
    name: 'ETooltip',
    inheritAttrs: false,

    props: {
        label: {
            type: String,
            default: undefined,
        },

        position: {
            type: String,
            default: 'is-top',
        },

        multilined: {
            type: Boolean,
            default: false,
        },

        type: {
            type: String,
            default: undefined,
        },

        size: {
            type: String,
            default: undefined,
        },

        delay: {
            type: Number,
            default: 0,
        },

        active: {
            type: Boolean,
            default: true,
        },

        dashed: {
            type: Boolean,
            default: false,
        },
    },

    emits: [],

    computed: {
        tooltipClasses() {
            const classes = ['tw:xl:max-w-fit'];

            if (this.dashed) {
                classes.push(
                    'tw:border-b',
                    'tw:border-dashed',
                    'tw:border-[var(--tooltip-dashed-border)]',
                    'tw:cursor-default',
                    'tw:inline-block'
                );
            }

            return classes.join(' ');
        },

        tooltipPosition() {
            const buefyPosition = this.position || '';

            const mapBuefyPositionToPrimeVuePosition = {
                'is-top': 'top',
                'is-bottom': 'bottom',
                'is-left': 'left',
                'is-right': 'right',
            };

            const defaultBuefyPosition = 'top';
            return mapBuefyPositionToPrimeVuePosition[buefyPosition] ?? defaultBuefyPosition;
        },

        tooltipModifier() {
            return { position: this.tooltipPosition };
        },

        tooltipValue() {
            return {
                value: this.label || '',
                disabled: this.active === false,
                pt: {
                    text: {
                        class: this.tooltipTextClasses,
                        style: this.tooltipTextStyle,
                    },
                },
                showDelay: this.delay || 0,
            };
        },

        tooltipTextClasses() {
            const classes = ['tw:text-sm'];

            if (this.multilined) {
                classes.push('tw:text-center');
            }

            return classes.join(' ');
        },

        tooltipTextStyle() {
            const style = {};

            if (this.multilined) {
                const size = this.size || '';

                const sizeMappings = {
                    'is-small': 'var(--tooltip-multiline-small)',
                    'is-medium': 'var(--tooltip-multiline-medium)',
                    'is-large': 'var(--tooltip-multiline-large)',
                };

                style.width = sizeMappings[size] || sizeMappings['is-small'];
                style['white-space'] = 'normal';
                style['text-align'] = 'center';
                style['text-wrap'] = 'balance';
                style['line-height'] = 'var(--tooltip-line-height)';
            }

            return style;
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';
@import 'resources/scss/_variables.scss';
@import 'resources/scss/_tooltip-variables.scss';

:root {
    --tooltip-primary-bg: #{$primary};
    --tooltip-primary-text: #{$white};
    --tooltip-info-bg: #{$info};
    --tooltip-info-text: #{$white};
    --tooltip-success-bg: #{$success};
    --tooltip-success-text: #{$white};
    --tooltip-warning-bg: #{$warning};
    --tooltip-warning-text: #{rgba($black, 0.7)};
    --tooltip-danger-bg: #{$danger};
    --tooltip-danger-text: #{$white};
    --tooltip-dark-bg: #{$black};
    --tooltip-dark-text: #{$white};
    --tooltip-light-bg: #{$grey-lightest};
    --tooltip-light-text: #{rgba($black, 0.7)};
    --tooltip-dashed-border: #{$grey-light};
    --tooltip-multiline-small: #{$tooltip-multiline-small};
    --tooltip-multiline-medium: #{$tooltip-multiline-medium};
    --tooltip-multiline-large: #{$tooltip-multiline-large};
    --tooltip-line-height: #{$tooltip-line-height};
}
</style>
